import React from 'react';
import { CallConfirmation as CallConfirmationType } from '../types';
import { CheckCircle, Phone, ArrowLeft, Lightbulb, Clock } from 'lucide-react';
import { formatMeetingTime } from '../utils/timezone';

interface CallConfirmationProps {
  confirmation: CallConfirmationType;
  onStartOver: () => void;
}

const CallConfirmation: React.FC<CallConfirmationProps> = ({ 
  confirmation, 
  onStartOver 
}) => {
  // Debug logging
  console.log('🎯 CallConfirmation rendered with:', {
    requestId: confirmation.requestId,
    hasRequestId: !!confirmation.requestId,
    confirmation: confirmation
  });

  return (
    <div className="min-h-screen gradient-pastel flex items-center justify-center p-4">
      <div className="card max-w-2xl w-full text-center fade-in">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-luma-success rounded-full flex items-center justify-center mx-auto mb-6 shadow-soft bounce-soft">
          <CheckCircle className="w-12 h-12 text-white" />
        </div>

        {/* Main Message */}
        <h1 className="text-3xl font-bold text-luma-text mb-4">
          Request Submitted Successfully!
        </h1>
        
        {confirmation.amount !== undefined && confirmation.amount > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-3 border-green-400 rounded-2xl p-6 mb-6 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-left flex-1">
                <h3 className="text-xl font-bold text-green-900 mb-2">💳 Payment Hold Placed</h3>
                <p className="text-green-800 font-semibold mb-3">
                  We have NOT charged your card yet!
                </p>
                <ul className="space-y-2 text-sm text-green-800">
                  <li className="flex items-start">
                    <span className="mr-2">✓</span>
                    <span>A temporary hold of <strong>${(confirmation.amount / 100).toFixed(2)}</strong> has been placed on your card</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">✓</span>
                    <span>You will <strong>only be charged</strong> when a doctor accepts your request</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">✓</span>
                    <span>If no doctor accepts, the hold will be <strong>automatically released</strong></span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
        
        {/* Reference Number - PROMINENT DISPLAY */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 rounded-xl p-6 mb-6 shadow-lg">
          <p className="text-sm text-gray-600 mb-2 font-medium">📋 Your Reference Number</p>
          <p className="font-mono text-2xl font-bold text-blue-600 mb-2 break-all">
            {confirmation.requestId || 'Loading...'}
          </p>
          {!confirmation.requestId && (
            <p className="text-xs text-red-600 mb-2">
              ⚠️ Request ID not found - check console logs
            </p>
          )}
          <p className="text-xs text-gray-600">
            ✅ Keep this number for your records
          </p>
        </div>
        
        <p className="text-lg text-luma-text-light mb-8">
          {confirmation.confirmationMessage || 'Your request has been received and a qualified health professional will call you soon.'}
        </p>

        {/* Request Details */}
        <div className="bg-luma-background rounded-3xl p-6 mb-8 text-left border border-luma-border">
          <h2 className="text-xl font-semibold text-luma-text mb-4 text-center">
            What happens next?
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center mr-4 mt-1 shadow-soft">
                <span className="text-white font-bold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-luma-text">Doctor Reviews Your Request</h3>
                <p className="text-sm text-luma-text-light">
                  A qualified doctor will review your symptoms and decide to accept your consultation request.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center mr-4 mt-1 shadow-soft">
                <span className="text-white font-bold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-luma-text">💳 Payment Charged (When Doctor Accepts)</h3>
                <p className="text-sm text-luma-text-light">
                  <strong className="text-green-600">Only when a doctor accepts</strong> will we charge your card $19.99. 
                  You'll receive a payment confirmation email.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center mr-4 mt-1 shadow-soft">
                <span className="text-white font-bold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-luma-text">Google Meet Link Sent</h3>
                <p className="text-sm text-luma-text-light">
                  You'll receive a Google Meet link via email for your video consultation.
                </p>
                {confirmation.scheduledMeetingDateTime && confirmation.timezone && (
                  <div className="mt-2 p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
                    <div className="flex items-start gap-3">
                      <Clock className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-600 font-medium mb-1">Your Consultation Session</div>
                        <div className="text-3xl font-bold text-blue-600 leading-tight mb-1">
                          {formatMeetingTime(confirmation.scheduledMeetingDateTime, confirmation.timezone).time}
                        </div>
                        <div className="text-base font-semibold text-gray-800">
                          {formatMeetingTime(confirmation.scheduledMeetingDateTime, confirmation.timezone).dayOfWeek}, {formatMeetingTime(confirmation.scheduledMeetingDateTime, confirmation.timezone).date}
                        </div>
                        <div className="text-sm text-gray-600 font-medium mt-1">
                          {formatMeetingTime(confirmation.scheduledMeetingDateTime, confirmation.timezone).timezone}
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                          Duration: 20 minutes
                        </div>
                        {/* Debug info */}
                        <div className="mt-3 p-2 bg-white rounded border border-blue-200">
                          <div className="text-xs text-gray-500 font-mono">
                            <div>UTC: {confirmation.scheduledMeetingDateTime}</div>
                            <div>Your TZ: {confirmation.timezone}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {!confirmation.scheduledMeetingDateTime && (
                  <p className="text-sm text-luma-text-light mt-1">
                    Expected time: <span className="font-semibold text-luma-primary">{confirmation.estimatedCallbackTime}</span>
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-8 h-8 gradient-primary rounded-full flex items-center justify-center mr-4 mt-1 shadow-soft">
                <span className="text-white font-bold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-semibold text-luma-text">Video Consultation</h3>
                <p className="text-sm text-luma-text-light">
                  Join the Google Meet at the scheduled time to discuss your concerns with the doctor.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Symptoms Summary */}
        {confirmation.symptoms && confirmation.symptoms.length > 0 && (
          <div className="bg-luma-info rounded-3xl p-6 mb-8 text-left border border-luma-border">
            <h3 className="font-semibold text-luma-text mb-3 text-center">
              Symptoms to Discuss
            </h3>
            <div className="flex flex-wrap justify-center gap-2">
              {confirmation.symptoms.map((symptom, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-luma-primary text-white text-sm rounded-full"
                >
                  {symptom}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Preparation Tips */}
        {confirmation.preparationTips && confirmation.preparationTips.length > 0 && (
          <div className="bg-yellow-50 rounded-xl p-6 mb-8 text-left">
            <div className="flex items-center mb-4">
              <Lightbulb className="w-5 h-5 text-yellow-600 mr-2" />
              <h3 className="font-semibold text-luma-text">
                Preparation Tips
              </h3>
            </div>
            <ul className="space-y-2">
              {confirmation.preparationTips.map((tip, index) => (
                <li key={index} className="flex items-start">
                  <span className="w-2 h-2 bg-yellow-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span className="text-sm text-luma-text">{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Important Notes */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
          <div className="flex items-start">
            <Phone className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
            <div className="text-left">
              <h4 className="font-semibold text-blue-800 mb-1">Important</h4>
              <p className="text-sm text-blue-700">
                Check your email for the Google Meet link. Make sure you have a stable internet connection 
                and a device with camera/microphone for the video consultation.
              </p>
            </div>
          </div>
        </div>

        {/* Emergency Notice */}
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-8">
          <p className="text-sm text-orange-800">
            <strong>For medical emergencies:</strong> Please call emergency services (911) 
            or visit your nearest emergency room immediately.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center">
          <button
            onClick={onStartOver}
            className="flex items-center justify-center px-6 py-3 border border-luma-primary text-luma-primary rounded-lg font-semibold hover:bg-luma-primary hover:text-white transition-all"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallConfirmation;